import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  InputAdornment,
  TextField
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import { V4_DIALOG_CONTENT } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { CopyToClipboardButton } from '../../elements';
import { AuthErrorCode, UserAdminErrorCode, extractApiErrorCode, extractApiErrorMessage } from '../../utils';
import { HealthSystemDomainUsersResultUserViewModel } from '../../api/HealthSystemApi';

interface InvitationLinkDialogProps {
  open: boolean;
  user: HealthSystemDomainUsersResultUserViewModel | null;
  onClose: () => void;
}

/**
 * Generates an invitation link for an invited user and shows it once so it can be handed over by
 * hand. The invitation that was emailed keeps working - this adds a link rather than replacing it.
 */
const InvitationLinkDialog: React.FC<InvitationLinkDialogProps> = ({ open, user, onClose }) => {
  const apiService = new ApiService();
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  const [isBusy, setIsBusy] = React.useState(false);
  const [link, setLink] = React.useState<string | null>(null);
  const [expiresAt, setExpiresAt] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const userId = user?.id ?? null;

  React.useEffect(() => {
    if (!open || userId === null) {
      return;
    }

    setIsBusy(true);
    setLink(null);
    setExpiresAt(null);
    setError(null);

    apiService.api.userAccounts
      .userAccountsCreateInvitationLink({ id: userId }, { format: 'json' })
      .then((result) => {
        setLink(result.data.link);
        setExpiresAt(result.data.expiresAt);
      })
      .catch((e: unknown) => {
        setError(getErrorMessage(e));
      })
      .finally(() => setIsBusy(false));
    // the link is minted per opening, so the effect keys on the user the dialog was opened for
  }, [open, userId]);

  const getErrorMessage = (e: unknown) => {
    switch (extractApiErrorCode(e)) {
      case AuthErrorCode.UserAccountNotFound:
        return t('f-usr-invitation-link-not-found');
      case AuthErrorCode.AccountAlreadyActivated:
        return t('f-usr-invitation-link-activated');
      case UserAdminErrorCode.Deactivated:
        return t('f-usr-invitation-link-deactivated');
      case UserAdminErrorCode.NotAllowedToModify:
        return t('f-usr-invitation-link-forbidden');
      default:
        return extractApiErrorMessage(e, { fallback: t('f-usr-invitation-link-error') });
    }
  };

  const userLabel = user?.name || user?.email || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <Grid item>
            <Icon color="primary">link</Icon>
          </Grid>
          <Grid item>{t('f-usr-invitation-link-title')}</Grid>
        </Grid>
      </DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        {isBusy && (
          <Grid container justifyContent="center" className="!py-8">
            <CircularProgress size={32} />
          </Grid>
        )}

        {!isBusy && error !== null && <Alert severity="error">{error}</Alert>}

        {!isBusy && error === null && link !== null && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">{t('f-usr-invitation-link-once')}</Alert>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('f-usr-invitation-link-for', { user: userLabel })}
                value={link}
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyToClipboardButton text={link} successMessage={t('f-usr-invitation-link-copied')} />
                      </InputAdornment>
                    )
                  }
                }}
                variant="outlined"
              />
            </Grid>

            {expiresAt !== null && (
              <Grid item xs={12}>
                <span className="text-sm text-gray-600">
                  {t('f-usr-invitation-link-expires', {
                    date: formatDate(expiresAt),
                    time: formatTime(expiresAt)
                  })}
                </span>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary" disabled={isBusy}>
          {t('f-com-close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvitationLinkDialog;
